'use client'

import dynamic from 'next/dynamic'
import { useEffect, useRef } from 'react'

import usePx from '@src/quince-site-common/src/hooks/usePx'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import type { TelemetryEventNames } from '@src/telemetry/telemetry-events.types'
import { trackEvent } from '@src/telemetry/track'

function QRCanvasComponent({
  url,
  widthInRem,
  className,
  margin = 4,
}: {
  url: string
  widthInRem: number
  className?: string
  /** Quiet-zone size in modules — the white border the qrcode library paints inside the
   *  canvas around the pattern. Default 4 (qrcode library default). This is the QR's only
   *  white border when the container has no padding; raise it for a thicker border, lower
   *  it for a thinner one. Note: 1 module ≈ a few px and scales with the QR's module count. */
  margin?: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Tracks which (url, outcome) telemetry has already fired. widthInPx changes on the
  // usePx font-size settle and on every (undebounced) resize, re-running the render
  // effect for the same url — without this guard each re-paint emits a duplicate event
  // whose payload is identical apart from event_id. Keying on outcome as well as url
  // means a url that errors then later succeeds still records its success.
  const trackedOutcomesRef = useRef(new Set<string>())
  const getPxFromRem = usePx()
  const widthInPx = getPxFromRem(widthInRem, false)

  useEffect(() => {
    const trackOnce = (event: TelemetryEventNames) => {
      const key = `${url}|${event}`
      if (trackedOutcomesRef.current.has(key)) return
      trackedOutcomesRef.current.add(key)
      trackEvent(event, {})
    }

    const generateQR = async () => {
      if (canvasRef.current) {
        const { toCanvas } = await import(/* webpackChunkName: "qrcode" */ 'qrcode')
        try {
          // Render the backing store at devicePixelRatio so the QR is crisp on retina.
          // qrcode pins BOTH the backing store and inline style.width/height to that hi-dpi
          // size, so restore the logical CSS size: every consumer still lays the QR out at
          // widthInPx (AppQRModal has no wrapper and relies on this) while the backing store
          // stays hi-dpi.
          const dpr = window.devicePixelRatio || 1
          await toCanvas(canvasRef.current, url, {
            width: widthInPx * dpr,
            margin,
          })
          canvasRef.current.style.width = `${widthInPx}px`
          canvasRef.current.style.height = `${widthInPx}px`
          trackOnce(TELEMETRY_EVENT_NAMES.QR_RENDERED_SUCCESS)
        } catch {
          trackOnce(TELEMETRY_EVENT_NAMES.QR_RENDERED_ERROR)
        }
      }
    }
    void generateQR()
  }, [url, widthInPx, margin])

  return <canvas ref={canvasRef} width={widthInPx} height={widthInPx} className={className} />
}

export default dynamic(() => Promise.resolve(QRCanvasComponent), {
  ssr: false,
})
